import { useState } from "react";
import { Plus, CheckCircle2, Circle, Heart, MessageSquare, Eye } from "lucide-react";
import { nf } from "@/lib/api";
import { VsAvg } from "@/components/calendar/PostResults";

const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const mondayOf = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); return addDays(x, -((x.getDay() + 6) % 7)); };

const PLAT_STYLE = { Instagram: "border-l-[#2A4B3C]", Facebook: "border-l-[#C05C3D]", "Instagram + Facebook": "border-l-[#C9A227]" };

const Card = ({ it, onOpen, onToggle, compact }) => {
  const done = it.status === "pubblicato";
  return (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", it.id)} onClick={() => onOpen(it)} data-testid={`cal-item-${it.id}`}
      className={`group bg-white border border-[#E8E8E4] border-l-4 ${PLAT_STYLE[it.platform] || ""} p-2 cursor-grab active:cursor-grabbing hover:border-[#BDBDB5] transition-colors ${done ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono-num text-[10px] text-[#5C5C58]">{it.time} · {it.platform === "Facebook" ? "FB" : it.platform === "Instagram" ? "IG" : "IG+FB"} · {it.format}</span>
        <button data-testid={`cal-toggle-${it.id}`} onClick={(e) => { e.stopPropagation(); onToggle(it); }} title={done ? "Segna come pianificato" : "Segna come pubblicato"}>
          {done ? <CheckCircle2 className="w-4 h-4 text-[#2A4B3C]" /> : <Circle className="w-4 h-4 text-[#BDBDB5] hover:text-[#2A4B3C]" />}
        </button>
      </div>
      <div className={`text-xs mt-1 leading-snug ${compact ? "line-clamp-2" : "line-clamp-3"} ${done ? "line-through" : ""}`}>{it.title}</div>
      {it.idea?._nuova && <span data-testid={`cal-new-badge-${it.id}`} className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wider text-[#8A6C14] border border-[#C9A227] px-1">Nuova idea AI</span>}
      {it.results?.length > 0 && (
        <div data-testid={`cal-results-${it.id}`} className="mt-1.5 pt-1.5 border-t border-[#F1F1EC] flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[#5C5C58] font-mono-num">
          <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" />{nf(it.results.reduce((a, r) => a + r.likes, 0))}</span>
          <span className="inline-flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{nf(it.results.reduce((a, r) => a + r.comments, 0))}</span>
          {it.results[0].reach > 0 && <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" />{nf(it.results.reduce((a, r) => a + r.reach, 0))}</span>}
          <VsAvg v={it.results[0].vs_avg} testid={`cal-vsavg-${it.id}`} />
        </div>
      )}
    </div>
  );
};

export const CalendarGrid = ({ start, weeks, items, onMove, onOpen, onToggle, onAdd }) => {
  const [over, setOver] = useState(null);
  const today = iso(new Date());
  const days = Array.from({ length: weeks * 7 }, (_, i) => addDays(start, i));
  const byDate = items.reduce((a, it) => ({ ...a, [it.date]: [...(a[it.date] || []), it] }), {});
  return (
    <div data-testid="calendar-grid">
      <div className="grid grid-cols-7 border-l border-t border-[#E8E8E4]">
        {DAY_NAMES.map((d) => <div key={d} className="label !text-[10px] px-3 py-2 border-r border-b border-[#E8E8E4] bg-[#FAFAF8]">{d}</div>)}
        {days.map((d) => {
          const k = iso(d);
          const list = byDate[k] || [];
          return (
            <div key={k} data-testid={`cal-day-${k}`}
              onDragOver={(e) => { e.preventDefault(); setOver(k); }} onDragLeave={() => setOver(null)}
              onDrop={(e) => { e.preventDefault(); setOver(null); onMove(e.dataTransfer.getData("text/plain"), k); }}
              className={`group/day border-r border-b border-[#E8E8E4] p-2 ${weeks === 1 ? "min-h-[360px]" : "min-h-[150px]"} transition-colors ${over === k ? "bg-[#EEF3EF]" : k < today ? "bg-[#FAFAF8]" : "bg-white"}`}>
              <div className="flex justify-between items-center mb-2">
                <span className={`font-mono-num text-xs ${k === today ? "bg-[#C05C3D] text-white px-1.5" : "text-[#5C5C58]"}`}>{d.getDate()}{d.getDate() === 1 || d === days[0] ? ` ${d.toLocaleDateString("it-IT", { month: "short" })}` : ""}</span>
                <button data-testid={`cal-add-${k}`} onClick={() => onAdd(k)} className="opacity-0 group-hover/day:opacity-100 transition-opacity text-[#5C5C58] hover:text-[#2A4B3C]"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="space-y-1.5">{list.map((it) => <Card key={it.id} it={it} onOpen={onOpen} onToggle={onToggle} compact={weeks > 1} />)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
